"use client";

import { SafeHydrate } from "@/SafeHydrate";
import React from "react";
import { motion, useReducedMotion } from "framer-motion";
import { Compass, BookOpen, Users, Globe as Globe2, Calendar, Scroll, Layers, Flame, Share2, ChartPie as PieChart, Handshake, Building2, Award } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
interface DepartmentItem {
  id: number;
  title: string;
  role: string;
  leader: string;
  iconName: string;
  fieldNote: string;
}
const departments: DepartmentItem[] = [{
  id: 1,
  title: "Ban Triết học Dịch lý",
  role: "Trưởng ban",
  leader: "PGS.TS Nguyễn Tài Đông",
  iconName: "BookOpen",
  fieldNote: "Nghiên cứu nền tảng triết học phương Đông và hệ thống nguyên lý Dịch học ứng dụng."
}, {
  id: 2,
  title: "Ban Khoa học Nhân sinh",
  role: "Trưởng ban",
  leader: "PGS.TS Phạm Thái Việt",
  iconName: "Users",
  fieldNote: "Nghiên cứu tác động của môi trường địa lý đối với sức khỏe và đời sống con người."
}, {
  id: 3,
  title: "Ban Địa lý Phong thủy",
  role: "Trưởng ban",
  leader: "TS Chu Xuân Giao",
  iconName: "Compass",
  fieldNote: "Nghiên cứu tương quan giữa phong thủy truyền thống và khoa học địa lý cảnh quan hiện đại."
}, {
  id: 4,
  title: "Ban Thiên văn Lịch pháp",
  role: "Trưởng ban",
  leader: "Phạm Khắc Tiệp",
  iconName: "Calendar",
  fieldNote: "Khảo cứu sự vận hành của thiên thể, chu kỳ thời gian và ứng dụng trong nông lịch cổ truyền."
}, {
  id: 5,
  title: "Ban Hán Nôm cổ học",
  role: "Trưởng ban",
  leader: "TS Nguyễn Hữu Mùi",
  iconName: "Scroll",
  fieldNote: "Dịch thuật, khảo cứu văn bia và văn bản Hán Nôm liên quan đến địa dư phong thủy nước nhà."
}, {
  id: 6,
  title: "Ban Khoa học Trái đất",
  role: "Trưởng ban",
  leader: "ThS Nguyễn Duy Tuấn",
  iconName: "Layers",
  fieldNote: "Đo đạc trường địa từ, cấu trúc địa tầng và phân tích các yếu tố sinh thái môi trường."
}, {
  id: 7,
  title: "Ban Tôn giáo Tín ngưỡng",
  role: "Trưởng ban",
  leader: "Thượng tọa ThS Thích Thanh Anh",
  iconName: "Flame",
  fieldNote: "Bảo tồn giá trị tâm linh truyền thống và mối liên hệ với không gian kiến trúc tôn giáo."
}, {
  id: 8,
  title: "Ban Truyền thông",
  role: "Trưởng ban",
  leader: "ThS Bùi Thế Long",
  iconName: "Share2",
  fieldNote: "Phổ biến kiến thức khoa học thường thức và quản lý các kênh thông tin chuyên đề."
}, {
  id: 9,
  title: "Ban Kế hoạch - Tài chính",
  role: "Trưởng ban",
  leader: "Vũ Đức Hào",
  iconName: "PieChart",
  fieldNote: "Hoạch định ngân sách đề tài nghiên cứu, quản lý tài chính và hỗ trợ hoạt động của Phân hội."
}, {
  id: 10,
  title: "Ban Hợp tác phát triển",
  role: "Trưởng ban",
  leader: "Nguyễn Quang Duy",
  iconName: "Handshake",
  fieldNote: "Mở rộng liên kết với các viện nghiên cứu, trường đại học và đối tác khoa học trong ngoài nước."
}, {
  id: 11,
  title: "Văn phòng",
  role: "Chánh Văn phòng",
  leader: "Cao Anh Hiếu",
  iconName: "Building2",
  fieldNote: "Điều phối hành chính, lưu trữ hồ sơ công văn và điều hành công tác sự vụ thường nhật."
}];
function renderDepartmentIcon(name: string) {
  const iconProps = {
    className: "size-5 text-primary"
  };
  switch (name) {
    case "BookOpen":
      return <BookOpen {...iconProps} />;
    case "Users":
      return <Users {...iconProps} />;
    case "Compass":
      return <Compass {...iconProps} />;
    case "Calendar":
      return <SafeHydrate><Calendar {...iconProps} /></SafeHydrate>;
    case "Scroll":
      return <Scroll {...iconProps} />;
    case "Layers":
      return <Layers {...iconProps} />;
    case "Flame":
      return <Flame {...iconProps} />;
    case "Share2":
      return <Share2 {...iconProps} />;
    case "PieChart":
      return <PieChart {...iconProps} />;
    case "Handshake":
      return <Handshake {...iconProps} />;
    case "Building2":
      return <Building2 {...iconProps} />;
    default:
      return <Globe2 {...iconProps} />;
  }
}
export default function Branch() {
  const shouldReduceMotion = useReducedMotion();
  const containerVariants = {
    hidden: {
      opacity: 0
    },
    show: {
      opacity: 1,
      transition: {
        staggerChildren: shouldReduceMotion ? 0 : 0.08
      }
    }
  };
  const itemVariants = {
    hidden: {
      opacity: 0,
      y: shouldReduceMotion ? 0 : 16
    },
    show: {
      opacity: 1,
      y: 0,
      transition: {
        duration: 0.4,
        ease: "easeOut"
      }
    }
  };
  return <section id="phân-hội-khoa-học-địa-lý-phong-thủy-việt-nam" data-nav="light" className="bg-background text-foreground py-16 md:py-24 border-t border-border">
      <div className="container mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
        {/* Header Block with Dual-Tone Anchor Line */}
        <div className="mb-12">
          <div className="inline-flex items-center gap-2 rounded-sm border border-border bg-muted/60 px-3 py-1 text-xs font-semibold uppercase tracking-wider text-muted-foreground">
            <Compass className="size-3.5 text-primary" />
            Đơn vị chuyên môn trực thuộc
          </div>

          <div className="mt-4 flex flex-col md:flex-row md:items-end md:justify-between gap-6">
            <div>
              <h2 className="text-2xl sm:text-3xl lg:text-4xl font-semibold tracking-tight text-secondary font-default">
                Phân hội Khoa học Địa lý Phong thủy Việt Nam
              </h2>
              <div className="mt-3 flex items-center gap-2">
                <div className="h-1 w-12 bg-secondary rounded-xs" />
                <div className="h-px flex-1 max-w-xs bg-border" />
              </div>
            </div>
            <p className="max-w-xl text-sm sm:text-base text-muted-foreground leading-relaxed">
              Tổ chức tập hợp các chuyên gia, nhà khoa học nghiên cứu liên ngành giữa địa lý sinh thái, cảnh quan môi trường và triết học cổ phương Đông.
            </p>
          </div>
        </div>

        {/* Leadership Highlight Banner */}
        <div className="mb-10 rounded-sm border border-border bg-card p-6 md:p-8 shadow-xs relative overflow-hidden">
          <div className="absolute top-0 left-0 h-full w-1.5 bg-primary" />
          <div className="grid grid-cols-1 md:grid-cols-12 gap-6 items-center">
            <div className="md:col-span-8 flex items-start gap-4">
              <div className="flex h-12 w-12 shrink-0 items-center justify-center rounded-sm bg-accent text-primary border border-border">
                <Award className="size-6" />
              </div>
              <div>
                <span className="text-xs font-medium uppercase tracking-wider text-muted-foreground block">
                  Lãnh đạo Phân hội
                </span>
                <h3 className="mt-1 text-xl sm:text-2xl font-semibold text-foreground font-default">
                  Chủ tịch Phân hội: Đỗ Đức Bình
                </h3>
                <p className="mt-2 text-sm text-muted-foreground leading-relaxed">
                  Chịu trách nhiệm điều hành toàn diện chương trình công tác, định hướng nghiên cứu lý luận và điều phối hoạt động giữa 10 Ban chuyên môn cùng Văn phòng thường trực.
                </p>
              </div>
            </div>
            <div className="md:col-span-4 flex md:justify-end">
              <div className="w-full md:w-auto rounded-sm border border-border bg-muted/40 p-4 text-xs text-muted-foreground">
                <div className="flex items-center justify-between gap-4 py-1 border-b border-border/60">
                  <span>Quy mô đơn vị:</span>
                  <span className="font-semibold text-foreground">10 Ban & 1 Văn phòng</span>
                </div>
                <div className="flex items-center justify-between gap-4 py-1 pt-2">
                  <span>Trạng thái hoạt động:</span>
                  <span className="font-semibold text-primary">Chính thức</span>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Directory Cards Grid */}
        <motion.div variants={containerVariants} initial="hidden" whileInView="show" viewport={{
        once: true,
        amount: 0.1
      }} className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
          {departments.map((dept, index) => <motion.div key={dept.id} variants={itemVariants} data-index={index}>
              <Card className="h-full border-border bg-card rounded-sm transition-[border-color,box-shadow] duration-200 hover:border-primary hover:shadow-sm border-t-2 border-t-primary/80">
                <CardHeader className="p-5 pb-3">
                  <div className="flex items-center justify-between gap-3">
                    <div className="flex h-10 w-10 items-center justify-center rounded-sm bg-accent border border-border">
                      {renderDepartmentIcon(dept.iconName)}
                    </div>
                    <span className="text-xs font-mono font-medium text-muted-foreground bg-muted/60 px-2 py-0.5 rounded-xs border border-border/60">
                      #{dept.id < 10 ? `0${dept.id}` : dept.id}
                    </span>
                  </div>
                  <CardTitle className="mt-3 text-base sm:text-lg font-semibold text-foreground font-default leading-snug">
                    {dept.title}
                  </CardTitle>
                </CardHeader>

                <CardContent className="p-5 pt-0 flex flex-col justify-between grow">
                  <div className="border-t border-border/60 pt-3 mt-1">
                    <div className="text-xs font-medium uppercase tracking-wider text-muted-foreground">
                      {dept.role}
                    </div>
                    <div className="mt-1 text-sm font-semibold text-foreground">
                      {dept.leader}
                    </div>
                  </div>
                  <p className="mt-3 text-xs text-muted-foreground leading-relaxed line-clamp-3">
                    {dept.fieldNote}
                  </p>
                </CardContent>
              </Card>
            </motion.div>)}
        </motion.div>
      </div>
    </section>;
}